import { apiFetch } from "./client";

/**
 * Sertifikat analize i izjava o NON GMO statusu, po LOT-u.
 *
 * Tri role i tri koraka. Laboratorija prilaže dokument, direktor kvaliteta ga pušta kupcu, kupac ga
 * preuzima — i samo pušten, i samo za LOT koji je njemu otpremljen. Dokument koji nije pušten kupac ne vidi
 * i ne zna da postoji.
 */

export const LOT_DOCUMENT_KINDS = ["COA", "NON_GMO", "HEALTH_CERTIFICATE", "OTHER"] as const;
export type LotDocumentKind = (typeof LOT_DOCUMENT_KINDS)[number];

/** PENDING = priložen i čeka, RELEASED = pušten kupcu, WITHDRAWN = bio pušten pa povučen. */
export type LotDocumentState = "PENDING" | "RELEASED" | "WITHDRAWN";

export type LotRef = {
  id: string;
  designation: string | null;
  productName: string | null;
  producedOn: string | null;
  expiresOn: string | null;
};

export type LotDocument = {
  id: string;
  goodsLotId: string;
  lotDesignation: string | null;
  lotProductName: string | null;
  kind: LotDocumentKind;
  issueNumber: string | null;
  validUntil: string | null;
  expired: boolean;
  fileName: string;
  contentType: string | null;
  sizeBytes: number | null;
  visibleToCustomer: boolean;
  approvedAt: string | null;
  uploadedAt: string;
  state: LotDocumentState;
};

export type CustomerLotDocument = {
  id: string;
  kind: LotDocumentKind;
  issueNumber: string | null;
  validUntil: string | null;
  expired: boolean;
  fileName: string;
  sizeBytes: number | null;
  approvedAt: string | null;
};

export type CustomerLotDocuments = {
  lot: LotRef;
  documents: CustomerLotDocument[];
};

export type CreateLotDocumentBody = {
  goodsLotId: string;
  kind: LotDocumentKind;
  objectKey: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  issueNumber?: string | null;
  validUntil?: string | null;
};

export type UpdateLotDocumentBody = {
  kind?: LotDocumentKind;
  issueNumber?: string | null;
  validUntil?: string | null;
};

function query(f: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const lotDocumentApi = {
  search: (f: { kind?: string; goodsLotId?: string; state?: string; q?: string }) =>
    apiFetch<LotDocument[]>(`/quality/lot-documents${query(f)}`),
  forLot: (goodsLotId: string) => apiFetch<LotDocument[]>(`/quality/lot-documents/lots/${goodsLotId}`),
  searchLots: (q: string) =>
    apiFetch<LotRef[]>(`/quality/lot-documents/lot-search?q=${encodeURIComponent(q)}`),

  /**
   * Fajl ide prvi, red drugi.
   *
   * Tabela ne drži dokument bez fajla, pa se red upisuje tek kad je fajl u skladištu — a tada se veličina i
   * tip mere ponovo, nad onim što je stvarno stiglo, ne nad onim što je pozivalac rekao da šalje.
   */
  presign: (body: { goodsLotId: string; fileName: string; contentType: string; sizeBytes: number }) =>
    apiFetch<{ objectKey: string; url: string }>("/quality/lot-documents/presign", {
      method: "POST",
      body,
    }),
  create: (body: CreateLotDocumentBody) =>
    apiFetch<LotDocument>("/quality/lot-documents", { method: "POST", body }),
  update: (id: string, body: UpdateLotDocumentBody) =>
    apiFetch<LotDocument>(`/quality/lot-documents/${id}`, { method: "PUT", body }),
  release: (id: string) =>
    apiFetch<LotDocument>(`/quality/lot-documents/${id}/release`, { method: "POST" }),
  withdraw: (id: string) =>
    apiFetch<LotDocument>(`/quality/lot-documents/${id}/withdraw`, { method: "POST" }),
  remove: (id: string) => apiFetch<void>(`/quality/lot-documents/${id}`, { method: "DELETE" }),
  downloadUrl: (id: string) => apiFetch<{ url: string }>(`/quality/lot-documents/${id}/file`),
};

/** Kupčeva strana: dokumenti za LOT-ove sa jednog njegovog računa. */
export const myLotDocumentApi = {
  forInvoice: (invoiceId: string) =>
    apiFetch<CustomerLotDocuments[]>(`/me/lot-documents?invoiceId=${encodeURIComponent(invoiceId)}`),
  /** Isto, za LOT-ove otpremljene za jednu njegovu porudžbinu. */
  forOrder: (orderId: string) =>
    apiFetch<CustomerLotDocuments[]>(`/me/lot-documents?orderId=${encodeURIComponent(orderId)}`),
  downloadUrl: (id: string) => apiFetch<{ url: string }>(`/me/lot-documents/${id}/file`),
};
